import { printCapitalGrowth } from "@/lib/print";

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function CapitalGrowth({ compact = false }) {
  const { user } = useAuth();
  const { currency, dateFormat, business, settings } = useSettings();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    opening_capital: "", additional_capital: "0", capital_withdrawn: "0"
  });
  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get("/capital-growth");
      setReport(data);
      setForm({
        opening_capital: data.configured ? String(data.opening_capital) : "",
        additional_capital: String(data.additional_capital),
        capital_withdrawn: String(data.capital_withdrawn)
      });
    } catch (e) {
      setFailed(true);
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const values = Object.values(form);
    if (values.some(v => v === "" || !Number.isFinite(Number(v)) || Number(v) < 0)) {
      toast.error("Enter valid capital amounts, zero or greater.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/capital-growth/settings", form);
      toast.success("Capital settings saved");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const money = value => fmtMoney(Number(value || 0), currency);
  if (loading && !report) return <div className="p-4 text-slate-500">Loading capital growth…</div>;
  if (failed) return <div className="p-4 border rounded-xl bg-white">
    Unable to load capital growth. <Button variant="outline" onClick={load}>Retry</Button>
  </div>;
  if (!report) return null;

  const cards = compact ? [
    ["Interest Reinvested", money(report.interest_reinvested)],
    ["Current Lending Capital", report.configured ? money(report.current_capital) : "Set opening capital"],
    ["Net Capital Growth", report.configured ? money(report.net_increase) : "—"]
  ] : [
    ["Opening Capital", report.configured ? money(report.opening_capital) : "Not set"],
    ["Additional Capital", money(report.additional_capital)],
    ["Interest Reinvested", money(report.interest_reinvested)],
    ["Capital Withdrawn", money(report.capital_withdrawn)],
    ["Current Lending Capital", report.configured ? money(report.current_capital) : "Not set"],
    ["Net Capital Growth", report.configured ? money(report.net_increase) : "—"]
  ];
  return <section className="space-y-4 mb-6">
    <div className="flex flex-wrap justify-between items-center gap-3">
      <h1 className={`font-heading font-bold text-navy ${compact ? "text-xl" : "text-3xl"}`}>Capital Growth</h1>
      {compact ? <Link className="text-maroon font-semibold underline" to="/capital-growth">View details</Link> :
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading || saving}>Refresh</Button>
          <Button variant="outline" disabled={loading || saving}
            onClick={() => printCapitalGrowth(report, business, settings)}>
            Print PDF
          </Button>
          {user?.role === "owner" && <Button className="bg-navy" disabled={saving}
            onClick={() => setEditing(!editing)}>{editing ? "Close" : "Edit Capital"}</Button>}
        </div>}
    </div>

    {!report.configured && <p className="text-sm text-amber-800 bg-amber-50 border rounded-lg p-3">
      Set the original capital before interest reinvestments to calculate your current lending capital.
    </p>}

    <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? "xl:grid-cols-3" : "xl:grid-cols-3"} gap-4`}>
      {cards.map(([label, value]) => <div key={label} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="mt-2 font-mono text-xl font-bold text-navy">{value}</div>
      </div>)}
    </div>

    {!compact && <>
      <p className="text-sm text-slate-600">
        Opening capital + additional capital + interest reinvested − capital withdrawn = current lending capital.
        Principal recycling reuses existing capital.
      </p>
      <p className="text-sm text-slate-500">
        Net growth includes additional capital and withdrawals.
        Growth against opening capital: <b>{report.growth_percent == null ? "—" : `${report.growth_percent}%`}</b>.
        This tracks recorded capital contributions and reinvestments; it is not your available cash or net assets after loan losses.
      </p>

      {editing && user?.role === "owner" && <div className="bg-white border rounded-xl p-5 space-y-4">
        <p className="text-sm text-slate-600">
          Enter original capital excluding all interest already recorded in Interest Recycling.
          Additional capital and withdrawals are lifetime totals. Update these totals when money is added or withdrawn.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[["Opening Capital", "opening_capital"], ["Additional Capital", "additional_capital"],
            ["Capital Withdrawn", "capital_withdrawn"]].map(([label, key]) =>
            <label key={key} className="text-sm text-navy">{label}
              <Input className="mt-2" type="number" min="0" step="0.01" value={form[key]}
                disabled={saving}
                onChange={e => setForm({ ...form, [key]: e.target.value })} />
            </label>)}
        </div>
        <Button className="bg-navy" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Capital"}</Button>
      </div>}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="p-4 border-b font-semibold text-navy">Interest Reinvestment History</div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] text-sm">
            <thead className="bg-slate-50"><tr>
              <th className="p-3 text-left">Sl.No</th><th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Notes</th><th className="p-3 text-right">Interest Reinvested</th>
              <th className="p-3 text-right">Total Interest Added to Capital</th>
            </tr></thead>
            <tbody>{report.records.map((r, i) => <tr className="border-t" key={r.id || i}>
              <td className="p-3">{i + 1}</td>
              <td className="p-3">{r.date ? fmtDate(r.date, dateFormat) : "—"}</td>
              <td className="p-3">{r.notes || "—"}</td>
              <td className="p-3 text-right font-mono">{money(r.amount)}</td>
              <td className="p-3 text-right font-mono">{money(r.cumulative_interest)}</td>
            </tr>)}
            {!report.records.length && <tr><td colSpan={5} className="p-8 text-center text-slate-500">
              No interest reinvestments recorded.
            </td></tr>}</tbody>
          </table>
        </div>
      </div>
      <p className="text-sm text-slate-500">
        Record interest reinvestments in the existing Interest Recycling section.
        Refresh this page to see additions, deletions or restored records.
        History totals include this business’s users.
      </p>
    </>}
  </section>;
}
